// Rich-text sanitization + safe HTML rendering for editor-authored content.
// Only inline/paragraph formatting is allowed (bold, italic, underline, line
// breaks, paragraphs, lists) — never scripts, styles, fonts, or arbitrary
// attributes. Uses DOMPurify (battle-tested) as the sanitization engine.
import DOMPurify from "dompurify";

const ALLOWED_TAGS = ["b", "strong", "i", "em", "u", "br", "p", "div", "span", "ul", "ol", "li"];

export function sanitizeHtml(html) {
  if (!html) return "";
  if (typeof window === "undefined" || !DOMPurify || !DOMPurify.sanitize) return "";
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [], // strip ALL attributes (no style/onclick/href/etc.)
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta"],
    KEEP_CONTENT: true, // unwrap disallowed tags but keep their text
    USE_PROFILES: { html: true },
  });
}

const escapeText = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const hasTags = (s) => /<[a-z][\s\S]*>/i.test(s || "");

// Convert a stored value into safe HTML. Legacy plain text (with newlines) is
// upgraded to paragraphs (\n\n) + line breaks (\n).
export function toDisplayHtml(value) {
  const s = String(value || "");
  if (!s) return "";
  if (hasTags(s)) return sanitizeHtml(s);
  return s
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeText(p).replace(/\n/g, "<br/>")}</p>`)
    .join("");
}

// Same normalization used to seed the contentEditable editor.
export const toEditorHtml = toDisplayHtml;

// Safe HTML renderer for the live site.
export const RichText = ({ html, className = "", style, testid, as: Tag = "div" }) => (
  <Tag
    className={`rich-text ${className}`.trim()}
    style={style}
    data-testid={testid}
    dangerouslySetInnerHTML={{ __html: toDisplayHtml(html) }}
  />
);
